import * as amplify from "../util/amplify.ts";
import ApiServer from "../ApiServer.ts";
import * as FwDatas from "../FwDatas.ts";
import * as modal from "../util/modal.ts";
import * as actionMenu from "../widget/action-menu.ts"
import Icons from "../svg/Icons.ts"
import { protoExtend } from "../lib/ObjectUtil.ts";
import { checkEventLimitOntry, modalEventHardLimitReached, modalOtherCircleEventHardLimitReached } from "./calendarclean.ts";
import { CalListCache } from "../server/EventStore.ts";
import * as RRuleHelper from "../lib/rrule/RRuleHelper.ts";
import * as segment from "../tracking-segment.ts";

/**
 * Shifts a date by the delta of a drag&drop.
 * Days/months/years are applied in UTC, so that a recurrency end date, which is an end-of-day UTC
 * for an all day event, does not land on another day.
 */
function shiftDate(date :Date,delta :Date){
  var result = new Date(date.getTime()+delta.milliseconds);
  result.setUTCFullYear(result.getUTCFullYear()+delta.years);
  result.setUTCMonth(result.getUTCMonth()+delta.months);
  result.setUTCDate(result.getUTCDate()+delta.days);
  return result;
}

// FWWEB-1820
/**
 * Builds the recurrency to send when the whole recurring event is copied to another date.
 * prepareEventInput drops the recurrency because the update calls handle it through their option,
 * so the copy has to rebuild it. The BYDAY/BYMONTHDAY of the source may not match the date the
 * event has been dropped on anymore, in that case only the frequency is kept, like the edit form
 * does (see widget/form/recurrency).
 */
function prepareRecurrencyForCopy(source,droppedDate,delta){
  var
    sourceRrule = source.rrule||'FREQ='+source.recurrency
      +';INTERVAL='+(source.recurrencyInterval||1)
      +(source.byDay?';BYDAY='+source.byDay:'')
      +(source.byMonthDay?';BYMONTHDAY='+source.byMonthDay:''),
    simplified = RRuleHelper.simplify(sourceRrule),
    options = new RRuleHelper.RRuleOptions(simplified),
    recurrency = RRuleHelper.asFamilywallForm(options.verify(droppedDate)?simplified:'FREQ='+options.freq);

  return new ApiServer.types.RecurrencyDescriptor({
    recurrency:recurrency.recurrency,
    recurrencyInterval:recurrency.recurrencyInterval,
    byDay:recurrency.byDay,
    byMonthDay:recurrency.byMonthDay,
    //the copied occurrences are the remaining ones shifted by the drop, so is the end of the recurrency
    recurrencyEndDate:source.recurrencyEndDate&&delta?shiftDate(source.recurrencyEndDate,delta):source.recurrencyEndDate
  });
}

function prepareEventInput(obj){
  var
    event = obj.event,
    allDay = event.allDay,
    start = null,
    end = null,
    dbEvent = FwDatas.get(event.id);

  if(allDay){
    var rawStart = new Date(event.start)
    var rawEnd = new Date(event.end)
    if(event.end===null){
      rawEnd = new Date(rawStart)
      rawEnd.setDate(rawEnd.getDate()+1);
    }
    start = new Date(rawStart.getTime()-(60000*rawStart.getTimezoneOffset()));
    end = new Date(rawEnd.getTime()-(60000*rawEnd.getTimezoneOffset())-1000);

  }else{
    start = event.start;
    if(dbEvent.allDay){
      end = new Date(start)
      end.setHours(end.getHours()+1)
    }else{
      end = event.end||new Date(event.start.getTime()+dbEvent.endDate.getTime()-dbEvent.startDate.getTime());
    }
  }

  var eventInput = protoExtend(dbEvent,{});
  eventInput.metaId = event.id;
  eventInput.startDate = start;
  eventInput.endDate = end;
  eventInput.allDay = allDay;
  //copy, the array comes from the cached event through the prototype
  eventInput.attendeeIds = [...(dbEvent.attendeeIds||[])];

  if(obj.oldResource){
    //this != is on purpose, it might be string or number
    eventInput.attendeeIds = eventInput.attendeeIds.filter(id=>id!=obj.oldResource.id)
  }
  if(obj.newResource){
    eventInput.attendeeIds.push(obj.newResource.id)
  }

  if(dbEvent.hasItsOwnRecurrency()){
    eventInput.recurrencyDescriptor = null;
  }
  //the input bean reads isToAll, not the toAll of the IEvent
  //an event dropped on another member column is no more for everybody
  //undefined lets the server compute the flag from the attendee list
  eventInput.isToAll = dbEvent.toAll && !obj.oldResource ? true : undefined;
  if(dbEvent.isTimetableEvent){
    eventInput.attendee = null
    eventInput.isToAll = true
  }else{
    eventInput.attendee = eventInput.attendeeIds.map(accountId=>{return {accountId}})
  }
  return eventInput;
}
/**
 * The task displayed in the calendar is prototype linked to the task (see EventStore), its assignee
 * are profiles: the input bean is built explicitly with the account ids.
 */
function getTaskAssigneeIds(task) :number[]{
  return (task.assignee||[]).map(assignee=>assignee.accountId);
}
/**
 * A drag or a resize changes the date, plus the attendee when dropped on another member column
 */
function trackEventMoved(obj){
  segment.track("Calendar Event Edited",{field_changed:obj.newResource?'date,attendee':'date'});
}
export async function eventModified(obj){
  var
    dbEvent = FwDatas.get(obj.event.id);

  if(dbEvent.metaId.indexOf('task/')===0){
    var result = new Date(dbEvent.dueDate);
    if(obj.delta){
      result.setDate(result.getDate() + obj.delta.days);
      result.setMonth(result.getMonth() + obj.delta.months);
      result.setFullYear(result.getFullYear() + obj.delta.years);
      result.setMilliseconds(result.getMilliseconds() + obj.delta.milliseconds);

      //a move only changes the due date and the assignee
      var
        taskInput = {taskId:dbEvent.metaId,dueDate:result,assignee:getTaskAssigneeIds(dbEvent),toAll:undefined},
        //resource ids are strings in fullcalendar
        oldAssignee = obj.oldResource && Number(obj.oldResource.id),
        newAssignee = obj.newResource && Number(obj.newResource.id);

      if(obj.oldResource){
        //a task dropped on another member column is no more for everybody
        taskInput.toAll = false;
        taskInput.assignee = taskInput.assignee.filter(accountId=>accountId!==oldAssignee);
      }
      if(obj.newResource && taskInput.assignee.indexOf(newAssignee)===-1){
        taskInput.assignee.push(newAssignee);
      }
      try{
        //refresh only once saved, otherwise the task comes back to its previous column
        await ApiServer.taskupdate2(taskInput)
        segment.track("Task Due Date Set");
        if(obj.newResource){
          segment.track("Task Assigned",{assignee_count:taskInput.assignee.length});
        }
      }finally{
        CalListCache.getCache(dbEvent.calendarId).invalidate()
        amplify.publish('eventUpdated')
      }
      return;
    }
    CalListCache.getCache(dbEvent.calendarId).invalidate()
    amplify.publish('eventUpdated')
  }else if(!dbEvent.hasItsOwnRecurrency()){
    Promise.resolve(ApiServer.evtupdate(null,prepareEventInput(obj))).then(()=>trackEventMoved(obj))
  }else{
    let eventInput = prepareEventInput(obj)
    var options = (await ApiServer.evtupdateoption(eventInput)).map(option=>{
      return {title:"event.updaterecurring"+option,onclick(){
        Promise.resolve(ApiServer.evtupdate(option,eventInput)).then(()=>trackEventMoved(obj))
        amplify.publish('eventUpdated')
      }}
    })
    if(options.length===1){
      options[0].onclick()
    }else{
      modal.ask({
        title:"actions.move",
        message:"event.moverecurringtext",
        button:options
      })
    }

  }

}
export async function copyEvent(obj){
  var
    dbEvent = FwDatas.get(obj.event.id);


  if(!dbEvent.hasItsOwnRecurrency()){
    let eventInput = prepareEventInput(obj)
    eventInput.metaId = undefined;
    if(!await checkEventLimitOntry(eventInput.familyId)){
      modalEventHardLimitReached(eventInput.familyId)
      amplify.publish('eventCreated') //to refresh fullcalendar
      return;
    }
    ApiServer.evtcreate.prepare({scope:eventInput.familyId})(eventInput)
    amplify.publish('eventCreated')
  }else{
    let eventInput = prepareEventInput(obj)
    eventInput.metaId = undefined;
    if(!await checkEventLimitOntry(eventInput.familyId)){
      modalEventHardLimitReached(eventInput.familyId)
      amplify.publish('eventCreated') //to refresh fullcalendar
      return;
    }
    modal.ask({
      title:"actions.copy",
      message:"event.copyrecurringtext",
      button:[
        {title:"event.updaterecurringOnlyOne",onclick(){
          eventInput.recurrencyDescriptor = new ApiServer.types.RecurrencyDescriptor({recurrency:'NONE'})
          ApiServer.evtcreate.prepare({scope:eventInput.familyId})(eventInput)
          amplify.publish('eventCreated')
        }},
        {title:"event.updaterecurringOnlyAfter",onclick(){
          eventInput.recurrencyDescriptor = prepareRecurrencyForCopy(dbEvent.recurrencyDescriptor,obj.event.start,obj.delta)
          ApiServer.evtcreate.prepare({scope:eventInput.familyId})(eventInput)
          amplify.publish('eventCreated')
        }}

      ]
    })
  }

}

export function eventDrop(obj){
  //a task can only be moved
  if(obj.oldResource || obj.event.id.indexOf('task/')===0){
    eventModified(obj)
    return;
  }

  document.body.insertAdjacentHTML('beforeend',`<div id="temp-container" style="position:fixed;left:0;right:0;top:0;bottom:0">
      <div id="mytag" style="position:absolute;left:${obj.jsEvent.pageX}px;top:${obj.jsEvent.pageY}px"></div>
    </div>`)
  function clean(){
    document.body.removeChild(document.getElementById('temp-container'))
  }
  var node = document.getElementById('mytag');
  actionMenu.initDom(node).open(obj.jsEvent,{
    menu:[
      {svg:Icons.ic_move, i18nkey:"actions.move",clickFunction:()=>{clean();eventModified(obj)}},
      {svg:Icons.ic_copy_one,i18nkey:"actions.copy",clickFunction:()=>{clean();copyEvent(obj)}}
    ],
    onCancel:()=>{
      clean();
      //TODO find good publish for just refreshing
      amplify.publish('eventCreated')
    }
  })
}